import { FormEvent, useEffect, useState } from 'react';
import { printInventoryLabelsWithFallback } from '@/lib/print.js';

export function PrintInventoryLabelsModal({
  open,
  onClose,
  items,
  title = 'Print labels',
}: {
  open: boolean;
  onClose: () => void;
  /** itemId + default quantity for each */
  items: Array<{ itemId: string; name?: string; quantity?: number }>;
  title?: string;
}) {
  const [rows, setRows] = useState<Array<{ itemId: string; name: string; qty: string }>>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows(
      items.map((i) => ({
        itemId: i.itemId,
        name: i.name ?? i.itemId.slice(0, 8),
        qty: String(i.quantity ?? 1),
      })),
    );
  }, [open, items]);

  if (!open) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = rows
        .map((r) => ({
          itemId: r.itemId,
          quantity: Math.max(1, Math.min(200, Number(r.qty) || 1)),
        }))
        .filter((r) => r.quantity > 0);
      await printInventoryLabelsWithFallback(payload);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
        <h3 className="text-lg font-bold text-slate-950">{title}</h3>
        <p className="mt-1 text-sm text-slate-600">
          One print job for all labels. Quantity is labels per item.
        </p>
        <form className="mt-4 space-y-3" onSubmit={(e) => void handleSubmit(e)}>
          {rows.map((r, idx) => (
            <div key={r.itemId} className="flex items-center gap-3">
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
                {r.name}
              </span>
              <input
                type="number"
                min={1}
                max={200}
                className="w-20 rounded-lg border border-slate-300 px-2 py-1 text-sm"
                value={r.qty}
                onChange={(e) => {
                  const next = [...rows];
                  next[idx] = { ...r, qty: e.target.value };
                  setRows(next);
                }}
              />
            </div>
          ))}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || rows.length === 0}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
            >
              {busy ? 'Printing…' : 'Print'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
